import { Logger } from 'src/app/modules/core/services/logger/logger.model';
import { DocumentSchema, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { FilterGroup, FilterGroupType } from 'src/app/modules/core/store/models/filters.model';
import {
  IFieldFilterHandler,
  AutoIncrementFieldFilterHandler,
  BooleanFieldFilterHandler,
  CategoryFieldFilterHandler,
  ColorFieldFilterHandler,
  DateTimeFieldFilterHandler,
  DistanceFieldFilterHandler,
  DocumentFieldFilterHandler,
  EmailFieldFilterHandler,
  GeometryFieldFilterHandler,
  ImageFieldFilterHandler,
  MultiPicklistFieldFilterHandler,
  NumberFieldFilterHandler,
  PhoneFieldFilterHandler,
  PicklistFieldFilterHandler,
  RelatedChildFieldFilterHandler,
  RelatedParentFieldFilterHandler,
  SingleReferenceFieldFilterHandler,
  StreamFieldFilterHandler,
  TextFieldFilterHandler,
  UIdFieldFilterHandler,
  UrlFieldFilterHandler,
  UserFieldFilterHandler,
  ArrayFieldFilterHandler,
  EnumerationFieldFilterHandler
} from 'src/app/modules/core/services/filter-runtime/field-handlers';
import { BusinessUnitsFieldFilterHandler } from './field-handlers/business-units-field-filter-handler';

export class FilterRuntimeService {
  private filterHandlers = {
    [FieldDataType.AutoIncrement]: new AutoIncrementFieldFilterHandler(),
    [FieldDataType.Boolean]: new BooleanFieldFilterHandler(),
    [FieldDataType.Category]: new CategoryFieldFilterHandler(),
    [FieldDataType.Color]: new ColorFieldFilterHandler(),
    [FieldDataType.DateTime]: new DateTimeFieldFilterHandler(),
    [FieldDataType.Distance]: new DistanceFieldFilterHandler(),
    [FieldDataType.Document]: new DocumentFieldFilterHandler(),
    [FieldDataType.Email]: new EmailFieldFilterHandler(),
    [FieldDataType.Geometry]: new GeometryFieldFilterHandler(),
    [FieldDataType.Image]: new ImageFieldFilterHandler(),
    [FieldDataType.MultiPicklist]: new MultiPicklistFieldFilterHandler(),
    [FieldDataType.Number]: new NumberFieldFilterHandler(),
    [FieldDataType.Phone]: new PhoneFieldFilterHandler(),
    [FieldDataType.Picklist]: new PicklistFieldFilterHandler(),
    [FieldDataType.RelatedChild]: new RelatedChildFieldFilterHandler(),
    [FieldDataType.RelatedParent]: new RelatedParentFieldFilterHandler(),
    [FieldDataType.SingleReference]: new SingleReferenceFieldFilterHandler(),
    [FieldDataType.Stream]: new StreamFieldFilterHandler(),
    [FieldDataType.Text]: new TextFieldFilterHandler(),
    [FieldDataType.UId]: new UIdFieldFilterHandler(),
    [FieldDataType.Url]: new UrlFieldFilterHandler(),
    [FieldDataType.User]: new UserFieldFilterHandler(),
    [FieldDataType.Array]: new ArrayFieldFilterHandler(),
    [FieldDataType.Enumeration]: new EnumerationFieldFilterHandler(),
    [FieldDataType.BusinessUnits]: new BusinessUnitsFieldFilterHandler()
  };

  constructor(private logger: Logger) {}

  documentMatchesFilter(
    document: any,
    filterGroup: FilterGroup,
    schema: DocumentSchema,
    geofences?: { [documentUId: string]: string }
  ): boolean {
    if (!filterGroup || (!(filterGroup.filters?.length > 0) && !(filterGroup.filterGroups?.length > 0))) {
      return true;
    }

    if (!!filterGroup.filters) {
      for (let filter of filterGroup.filters.filter(f => !!f.field)) {
        let fieldDataType = FieldDataType.Text; // default field type
        const fieldValue = document[filter.field];

        const schemaField = schema.fields.find(f => f.systemName == filter.field);
        if (!schemaField) {
          this.logger
            .withMethod('documentMatchesFilter')
            .logError(`Filter Field: ${filter?.field} was not found in the document or the schema.`);
          continue;
        }

        fieldDataType = schemaField.dataType;

        var filterHandler = this.filterHandlers[fieldDataType] as IFieldFilterHandler;
        if (!filterHandler) {
          this.logger
            .withMethod('documentMatchesFilter')
            .logError(`Filter Handler for Field: ${filter?.field} was not registered and will be ignored.`);
          continue;
        }

        try {
          var filterDoesMatch = filterHandler.fieldMatchesFilter(fieldValue, filter, geofences);

          // if (!filterDoesMatch) {
          //   this.logger
          //     .withMethod('documentMatchesFilter')
          //     .logInfo(`Filter Field: ${filter?.field} does not match Filter: Operation=${filter.operator} Value=${filter.value}.`);
          // }

          if (filterDoesMatch && filterGroup.groupType == FilterGroupType.Or) {
            // check for "Or" type. If so, and the filter is true then we can stop
            // since we had at least one match so the group succeeds
            return true;
          } else if (!filterDoesMatch && filterGroup.groupType == FilterGroupType.And) {
            // check for "And" type. If so, and the filter is false then we can stop
            // since we had at least one failure so the group fails
            return false;
          }
        } catch (e) {
          this.logger.withMethod('documentMatchesFilter').logError(`Filter Handler for Field: ${filter?.field} caused an error: ${e}`);
          continue;
        }
      }
    }

    if (!!filterGroup.filterGroups) {
      for (let childFilterGroup of filterGroup.filterGroups) {
        let filterDoesMatch = this.documentMatchesFilter(document, childFilterGroup, schema, geofences);

        if (filterDoesMatch && filterGroup.groupType == FilterGroupType.Or) {
          // check for "Or" type. If so, and the filter is true then we can stop
          // since we had at least one match so the group succeeds
          return true;
        } else if (!filterDoesMatch && filterGroup.groupType == FilterGroupType.And) {
          // check for "And" type. If so, and the filter is false then we can stop
          // since we had at least one failure so the group fails
          return false;
        }
      }
    }

    // if we made it to the end then we need to check for success or failure.
    // if "Or" and we are here then we did not have at least one success so we need to fail.
    // if "And" and we are here then we did not have at last one failure so we need to succeed.
    if (filterGroup.groupType == FilterGroupType.Or) {
      return false;
    } else {
      return true;
    }
  }
}
